/**
 * Utilidades para manejo de fechas y tiempos
 */

/**
 * Formatea tiempo en segundos a formato HH:MM:SS
 * @param {number} time - Tiempo en segundos
 * @returns {string} Tiempo formateado como HH:MM:SS
 */
export const formatTime = (time) => {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = time % 60;
  return `${hours < 10 ? "0" + hours : hours}:${
    minutes < 10 ? "0" + minutes : minutes
  }:${seconds < 10 ? "0" + seconds : seconds}`;
};

/**
 * Formatea fecha a formato DD-MM-YYYY
 * @param {string|Date} fechaInput - Fecha de entrada
 * @returns {string} Fecha formateada como DD-MM-YYYY
 */
export const formatFechaDDMMYYYY = (fechaInput) => {
  const fecha = new Date(fechaInput);
  if (isNaN(fecha)) return "Fecha inválida";

  const dia = String(fecha.getDate()).padStart(2, "0");
  const mes = String(fecha.getMonth() + 1).padStart(2, "0");
  const anio = fecha.getFullYear();

  return `${dia}-${mes}-${anio}`;
};

/**
 * Formatea fecha y hora
 * @param {string|Object} dateTimeString - String de fecha y hora o objeto con propiedad date
 * @returns {string} Fecha y hora formateada
 */
export function formatDateTime(dateTimeString) {
  // Verificar si dateTimeString es un objeto y tiene la propiedad 'date'
  const dateStr = dateTimeString?.date || dateTimeString;

  // Verificar si dateStr es válido y es un string
  if (!dateStr || typeof dateStr !== "string") return "N/A"; // Valor predeterminado

  const [datePart, timePartWithMs] = dateStr.split(" ");
  const timePart = timePartWithMs.split(".")[0]; // Eliminar los milisegundos
  const [year, month, day] = datePart.split("-");
  return `${day}-${month}-${year} ${timePart}`;
}

/**
 * Obtiene la diferencia de tiempo formateada entre dos fechas
 * @param {string|Date} inicio - Fecha de inicio
 * @param {string|Date} fin - Fecha de fin
 * @returns {string} Diferencia formateada como HH:MM:SS
 */
export function getFormattedTimeDifference(inicio, fin) {
  const start = new Date(inicio);
  const end = new Date(fin);
  const diffMs = end - start; // Diferencia en milisegundos
  const diffSecs = Math.round(diffMs / 1000); // Diferencia en segundos

  const hours = Math.floor(diffSecs / 3600);
  const minutes = Math.floor((diffSecs % 3600) / 60);
  const seconds = diffSecs % 60;

  const formattedDiff = `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

  return formattedDiff;
}

/**
 * Formatea una fecha a formato YYYY-MM-DD HH:MM:SS
 * @param {Date} fecha - Objeto Date
 * @returns {string} Fecha formateada
 */
export function newDateFormated(fecha) {
  const formattedDate =
    fecha.getFullYear() +
    "-" +
    ("0" + (fecha.getMonth() + 1)).slice(-2) +
    "-" +
    ("0" + fecha.getDate()).slice(-2) +
    " " +
    ("0" + fecha.getHours()).slice(-2) +
    ":" +
    ("0" + fecha.getMinutes()).slice(-2) +
    ":" +
    ("0" + fecha.getSeconds()).slice(-2);
  return formattedDate;
}

/**
 * Obtiene la fecha y hora actual formateada
 * @returns {string} Fecha y hora actual
 */
export function getCurrentDateTime() {
  const currentDateTime = new Date();
  const date = currentDateTime.toLocaleDateString();
  const time = currentDateTime.toLocaleTimeString();
  return `${date} ${time}`;
}

/**
 * Formatea un objeto de fecha
 * @param {Object} dateObj - Objeto de fecha con propiedad date
 * @returns {string} Fecha formateada o "Fecha Inválida"
 */
export function formatDate(dateObj) {
  // Si el objeto es válido y tiene la propiedad date, la usamos; si no, retornamos "Fecha Inválida".
  if (dateObj && dateObj.date) {
    const date = new Date(dateObj.date);
    if (!isNaN(date)) {
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
    }
  }

  console.error("Fecha inválida detectada:", dateObj);
  return "Fecha Inválida";
}

/**
 * Formatea una fecha de entrada
 * @param {any} fechaInput - Fecha de entrada en cualquier formato
 * @returns {string} Fecha formateada o mensaje de error
 */
export function formatearFecha(fechaInput) {
  if (!fechaInput) return "";

  try {
    // 1) Si es un objeto con la propiedad .date, úsala
    let fechaRaw;
    if (typeof fechaInput === "object" && fechaInput.date) {
      fechaRaw = fechaInput.date; // ej: "2025-06-16 08:38:46.000000"
    } else if (typeof fechaInput === "string") {
      fechaRaw = fechaInput;
    } else {
      // si ya es un Date
      return fechaInput.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }

    // 2) Quedarnos sólo con la parte de fecha (YYYY-MM-DD)
    const soloFecha = fechaRaw.split(" ")[0];
    // 3) Crear un Date válido
    const fecha = new Date(soloFecha + "T00:00:00");

    if (isNaN(fecha.getTime())) {
      console.warn("Fecha inválida:", fechaInput);
      return "Fecha inválida";
    }

    // 4) Devolver formato dd/MM/yyyy
    return fecha.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (error) {
    console.error("Error al formatear fecha:", fechaInput, error);
    return "Error fecha";
  }
}

/**
 * Verifica si una fecha es hoy
 * @param {any} fechaInput - Fecha de entrada en cualquier formato
 * @returns {boolean} true si la fecha es hoy, false en caso contrario
 */
export function esHoy(fechaInput) {
  if (!fechaInput) return false;

  let raw;
  if (typeof fechaInput === "object" && fechaInput.date) {
    raw = fechaInput.date;
  } else if (typeof fechaInput === "string") {
    raw = fechaInput;
  } else if (fechaInput instanceof Date) {
    raw = null;
  } else {
    return false;
  }

  let fechaObj;
  if (raw) {
    const soloFecha = raw.split(" ")[0];
    fechaObj = new Date(soloFecha + "T00:00:00");
  } else {
    fechaObj = new Date(fechaInput);
  }

  if (isNaN(fechaObj.getTime())) {
    console.warn("esHoy: fecha inválida ->", fechaInput);
    return false;
  }

  const hoy = new Date();
  const hoyFormateado = new Date(
    hoy.getFullYear(),
    hoy.getMonth(),
    hoy.getDate()
  );
  const fechaFormateada = new Date(
    fechaObj.getFullYear(),
    fechaObj.getMonth(),
    fechaObj.getDate()
  );

  return fechaFormateada.getTime() === hoyFormateado.getTime();
}

/**
 * Obtiene la fecha y hora actual en formato ISO local
 * @returns {string} Fecha y hora actual en formato YYYY-MM-DD HH:MM:SS
 */
export function getCurrentLocalISODateTime() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}
